import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Sidebar from "./Sidebar";
import UserProfile from "./UserProfile";
import ChatbotWidget from "../Common/ChatbotWidget";
import "./DashboardLayout.css";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [chatbotOpen, setChatbotOpen] = useState(false);
  const showSidebar = true;

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (showSidebar && isMobile && sidebarOpen) {
      document.body.classList.add("sidebar-open");
    } else {
      document.body.classList.remove("sidebar-open");
    }
  }, [isMobile, sidebarOpen, showSidebar]);

  return (
    <div className="dashboard-container">
      {/* Top Header - ALWAYS VISIBLE AT TOP LEVEL */}
      <header className="dashboard-header">
        <div className="header-inner">
          {/* Left Section */}
          <div className="header-left">
            {/* Mobile Hamburger - Only visible on mobile */}
            {isMobile && (
              <button
                className="mobile-hamburger-btn"
                onClick={toggleSidebar}
                aria-label="Toggle Menu"
              >
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
                <div className="hamburger-line"></div>
              </button>
            )}

            <div className="header-brand">
              <img
                src="/images/thriftlogo.png"
                alt="Thriftops"
                className="header-logo"
              />
            </div>
          </div>

          {/* Center Section - Search Bar */}
          <div className="header-center">
            <div className="search-container">
              <div className="search-input-wrapper">
                <svg
                  className="search-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="8"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="m21 21-4.35-4.35"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search products, orders, customers..."
                />
              </div>
            </div>
          </div>

          {/* Right Section */}
          <div className="header-right">
            <button className="chatbot-btn" aria-label="Chat Support" onClick={() => setChatbotOpen((prev) => !prev)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
                <path
                  d="M9 14c.5.5 1.5 1 3 1s2.5-.5 3-1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button className="notification-btn" aria-label="Notifications">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M13.73 21a2 2 0 0 1-3.46 0"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </button>
            <UserProfile />
          </div>
        </div>
      </header>

      {/* Wrapper for Sidebar + Main Content */}
      <div className={`dashboard-wrapper ${showSidebar ? "" : "no-sidebar"}`}>
        {showSidebar && (
          <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
        )}

        <div
          className={`dashboard-main ${
            showSidebar
              ? sidebarOpen
                ? "sidebar-expanded"
                : "sidebar-collapsed"
              : "no-sidebar"
          }`}
        >
          {/* Mobile Hamburger Below Header - Only visible on mobile when sidebar is closed */}
          {showSidebar && isMobile && !sidebarOpen && (
            <div className="mobile-content-hamburger">
              <button
                className="content-hamburger-btn"
                onClick={toggleSidebar}
                aria-label="Toggle Menu"
              >
                <div className="hamburger-icon-line"></div>
                <div className="hamburger-icon-line"></div>
                <div className="hamburger-icon-line"></div>
              </button>
            </div>
          )}

          {/* Main Content Area */}
          <main className="dashboard-content">
            <Outlet />
          </main>
        </div>
      </div>

      <ChatbotWidget
        isOpen={chatbotOpen}
        onToggle={() => setChatbotOpen((prev) => !prev)}
        onClose={() => setChatbotOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
